import {Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {ApiService} from '../../services/api/api.service';
import {Subject, Subscription} from 'rxjs';
import {DateTimePeriodService} from '../../services/date-time-period.service';
import {Series} from '../../_models/series';
import {MatTableDataSource} from "@angular/material/table";
import {MatSort} from "@angular/material/sort";
import {MatPaginator} from "@angular/material/paginator";
import {take, takeUntil, tap} from 'rxjs/operators';
import {EventService} from "../../services/event.service";
import {Event as WireEvent} from "../../_models/event";
import {SeriesAuditHistoryService} from "./series-audit-history.service";
import {IDateTimePeriod} from "../../_typing/date-time-period";
import {DateTimeInstanceService} from "../../services/date-time-instance.service";
import {
    SeriesAuditBaseTableConfig,
    SeriesAuditHistoryBaseTableComponent
} from "./series-audit-history-base-table/series-audit-history-base-table.component";

import {MatTooltip} from "@angular/material/tooltip";
import {NameOrDescriptionPipe, StringDatePipe} from "../../shared/pipes";
import {
    ConsolidatedSeriesAuditHistoryComponent
} from "./consolidated-series-audit-history/consolidated-series-audit-history.component";

export interface TableRowData {
    type: string | null
    value: any | null,
    title?: string,
    name: string
}

export interface SeriesAuditHistoryTable {
    timestamp: Date;
    data_point_timestamp?: Date;
    value: number | null;
    old_value: number | null;
    changed_on: Date;
    changed_by: string;
    comments: string[];
}

export interface SeriesAuditStatistics {
    "first_timestamp": string;
    "last_timestamp": string;
    "first_value": number;
    "last_value": number;
    "first_upload_by": string;
    "last_upload_by": string;
    "total_points": number;
    "user_stats": [{
        "user": string;
        "percentage": number;
    }];
    id:string;
}

@Component({
    selector: 'series-audit-history',
    templateUrl: './series-audit-history.component.html',
    styleUrls: ['./series-audit-history.component.scss'],
    imports: [
    MatTooltip,
    NameOrDescriptionPipe,
    StringDatePipe,
    SeriesAuditHistoryBaseTableComponent,
    ConsolidatedSeriesAuditHistoryComponent
],
    encapsulation: ViewEncapsulation.None
})
export class SeriesAuditHistoryComponent implements OnInit {
    private onDestroy: Subject<void> = new Subject();
    page_number: number = 0;
    page_size = 10;
    paged_history: any[];
    current_page: any;
    times: any = []

    constructor(private api: ApiService,
                private dateTimePeriodService: DateTimePeriodService,
                private eventService: EventService,
                private cps: SeriesAuditHistoryService,
                private dateInst: DateTimeInstanceService,
    ) {
    }

    _series: Series;

    get series(): Series {
        return this._series;
    }

    @Input()
    set series(_series: Series) {
        this._series = _series;
    }

    @Input() dtp: IDateTimePeriod;
    @Input() table_view: boolean = true;
    config: SeriesAuditBaseTableConfig;
    dataSource: MatTableDataSource<SeriesAuditHistoryTable>;
    columns = ['timestamp', 'value', 'old_value', 'changed_by', 'changed_on', 'comments'];
    comments: WireEvent[];

    @ViewChild(MatPaginator) paginator: MatPaginator;

    private sort;

    @ViewChild(MatSort) set content(content: ElementRef) {
        this.sort = content;
        if (this.sort && this.dataSource) {
            this.dataSource.sort = this.sort;
        }
    }

    ngOnInit(): void {
        this.page_number = 0;
        this.dataSource = new MatTableDataSource();

        this.dateInst.dateTimePeriodRefreshed$.pipe(takeUntil(this.onDestroy)).subscribe((dtp: IDateTimePeriod) => {
            this.dateInst.dtp = dtp;

            if (!this.table_view) {
                this.cps.onSeriesChanged(this.series, dtp, this.table_view);
            } else {
                this.config = {type: 'series', dtp: dtp};
            }
        })

        if (!this.table_view) {
            this.cps.$auditHistoryData.pipe(takeUntil(this.onDestroy),
                tap(data => {
                    this.paged_history = data;
                })
            ).subscribe();
            this.cps.onSeriesChanged(this.series, this.dateInst.dtp, this.table_view);
        }
        this.dateInst.dateTimePeriodInitialised$.pipe(take(1)).subscribe((dtp) => {
            this.config = {type: 'series', dtp: this.dateInst.dtp};
        })
    }

    getNext() {
        this.current_page = this.paged_history[this.page_number];
    }

    page(dir) {
        const ctrl = this;
        ctrl.page_number = ctrl.page_number + (dir * 1);
        ctrl.getNext();
    }

    showPrevious() {
        return this.page_number == 1 ? false : true;
    }

    applyFilter(filterValue: any) {
        filterValue = filterValue.trim().toLowerCase();
        this.dataSource.filter = filterValue;
    }

    ngOnDestroy(): void {
        if (this.onDestroy) {
            this.onDestroy.next();
            this.onDestroy.complete();
        }
    }
}
